import { Box, Button, CircularProgress, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import { InfoTooltip } from 'components/InfoTooltip';
import React, { type ComponentType, type JSX } from 'react';
import { CopyButton } from './CopyButton';
import { ReactComponent as SparklingIcon } from 'assets/icons/sparkling_icon.svg';
import Markdown, { ExtraProps } from 'react-markdown';

type Components = {
  [Key in keyof JSX.IntrinsicElements]?:
    | ComponentType<JSX.IntrinsicElements[Key] & ExtraProps>
    | keyof JSX.IntrinsicElements;
};

const defaultMarkdownElementProps = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  fontFamily: ['Lato', 'Arial', 'sans-serif'].join(','),
};

const markdownComponents: Components = {
  ul: ({ node, ...props }) => (
    <ul
      style={{
        ...defaultMarkdownElementProps,
        marginBlockStart: 0,
        paddingInlineStart: '25px',
        marginBlockEnd: 0,
      }}
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol
      style={{
        ...defaultMarkdownElementProps,
        marginBlockStart: 0,
        paddingInlineStart: '25px',
        marginBlockEnd: 0,
      }}
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p
      style={{
        ...defaultMarkdownElementProps,
        margin: 0,
      }}
      {...props}
    />
  ),
};

export const TargetingSuggestions = ({
  suggestions = [],
  isLoading,
  onGenerate,
}: {
  suggestions?: string[];
  isLoading?: boolean;
  onGenerate: () => void;
}) => {
  return (
    <Stack gap={4}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight={400} sx={{ mr: 1 }}>
          Textvorschläge – generiert basierend auf deiner Website und Creatives
        </Typography>
        <Box>
          <InfoTooltip
            placement="bottom"
            text={
              <Typography variant="body2">
                Basierend auf deiner Webseite und Creatives haben wir AI generierte Vorschläge für dich erstellt.
                <br />
                <br />
                Du kannst sie kopieren und inhaltlich an deine Kampagne anpassen.
              </Typography>
            }
          />
        </Box>
      </Box>
      <Stack rowGap={1} columnGap={2} direction={{ md: 'row' }} sx={{ m: -2 }} flexWrap="wrap">
        {suggestions.map((suggestion, idx) => {
          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                flexBasis: '50%',
                background: '#E3F2FD',
                borderRadius: '12px',
                p: 4,
                flex: 1,
                minWidth: 300,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                m: 2,
              }}
            >
              <Stack gap={0.5} direction="row" alignItems="start" alignContent="center">
                <SparklingIcon />
                &nbsp;
                <Typography color="info.dark" variant="body2" fontWeight={700}>
                  Vorschlag {idx + 1}
                </Typography>
              </Stack>
              <Markdown components={markdownComponents}>{suggestion}</Markdown>
              <CopyButton text={suggestion} />
            </Paper>
          );
        })}
        {isLoading
          ? // if 1 of 2 suggestions is loading, show 1 or 2 skeletons accordingly to prevent empty row space
            (suggestions.length % 2 === 1 ? [''] : ['', '']).map((_, idx) => (
              <Paper
                key={suggestions.length + idx + 1 + '-empty'}
                elevation={0}
                sx={{
                  flexBasis: '50%',
                  background: '#E3F2FD',
                  borderRadius: '12px',
                  p: 4,
                  flex: 1,
                  minWidth: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  m: 2,
                }}
              >
                <Stack gap={0.5} direction="row" alignItems="start" alignContent="center" mb={1}>
                  <SparklingIcon />
                  &nbsp;
                  <Typography color="info.dark" variant="body2" fontWeight={700}>
                    Vorschlag {suggestions.length + idx + 1}
                  </Typography>
                </Stack>
                <Skeleton variant="text" sx={{ fontSize: '16px', width: '98%' }} />
                <Skeleton variant="text" sx={{ fontSize: '16px', width: '90%' }} />
                <Skeleton variant="text" sx={{ fontSize: '16px', width: '93%' }} />
                <Skeleton variant="text" sx={{ fontSize: '16px', width: '33%', margin: '10px 0 0 0' }} />
              </Paper>
            ))
          : null}
      </Stack>
      <Button
        startIcon={
          isLoading ? (
            <CircularProgress size="1rem" sx={(theme) => ({ color: theme.palette.grey[400] })} />
          ) : (
            <Autorenew />
          )
        }
        variant="outlined"
        disabled={isLoading}
        size="small"
        sx={(theme) => ({
          marginTop: '8px',
          color: isLoading ? theme.palette.grey[400] : 'primary.main',
          borderColor: isLoading ? theme.palette.grey[400] : 'primary.main',
          fontSize: '12px',
          width: { xs: '100%', sm: !isLoading ? '266px' : '226px' },
        })}
        onClick={(e) => {
          e.preventDefault();

          onGenerate();
        }}
      >
        {isLoading ? 'Texte werden generiert.....' : 'WEITERE vorschläge GENERIEREN'}
      </Button>
    </Stack>
  );
};
